import { useMemo } from 'react';

import { LegendInterval, VectileLayers } from '@hcs/types';

import { getLegendBreaksForMetric, latLngToTileCoords } from '../utils';

import { useHcMap } from './useHcMap';
import { useHcMapLayers } from './useHcMapLayers';
import { useVectilesStats } from './useVectilesStats';

export const useHcMapLayerLegendBreaks = (
  mapId: string,
): LegendInterval[] | undefined => {
  const { vectilesLayerSource } = useHcMapLayers(mapId);
  const { mapState } = useHcMap(mapId);
  const { viewport, boundsInfo, heatmap, heatmapTheme } = mapState || {};
  const vectilesMetricId = heatmap?.vectilesMetricId;
  const propertyType = heatmap?.propertyType;
  const integerZoom = viewport?.zoom ? Math.floor(viewport.zoom) : 0;

  const tileNE = boundsInfo?.northEast
    ? latLngToTileCoords({
        lat: boundsInfo.northEast.lat,
        lng: boundsInfo.northEast.lng,
        zoom: integerZoom,
      })
    : undefined;
  const tileSW = boundsInfo?.southWest
    ? latLngToTileCoords({
        lat: boundsInfo.southWest.lat,
        lng: boundsInfo.southWest.lng,
        zoom: integerZoom,
      })
    : undefined;

  const tileRangeURLSegment = `${tileSW?.x}-${tileNE?.x}/${tileNE?.y}-${tileSW?.y}`;

  const { data, isLoading } = useVectilesStats(
    vectilesLayerSource?.vectilesLayer || VectileLayers.HCS_BLOCKS,
    integerZoom,
    tileRangeURLSegment,
  );

  /* Only get breaks if zoom level is within range for active metric or if not metric is passed,
   * use the default zoom level -> source keyword mapping
   */
  const intervals: LegendInterval[] | undefined = useMemo(() => {
    return tileNE &&
      tileSW &&
      tileSW.x > 0 &&
      tileNE.x > 0 &&
      data &&
      !isLoading &&
      data?.property_stats &&
      vectilesMetricId &&
      heatmapTheme
      ? getLegendBreaksForMetric({
          vectilesMetricId,
          breaksMapping: data?.property_stats,
          propertyType,
          heatmapTheme,
        })
      : undefined;
  }, [
    data,
    heatmapTheme,
    isLoading,
    propertyType,
    tileNE,
    tileSW,
    vectilesMetricId,
  ]);

  return intervals;
};
